/**
 * Hand-rolled parser for Mermaid `sequenceDiagram` text → an ordered event model.
 *
 * Covers the grammar LLMs actually emit: participants/actors (+aliases), messages
 * (sync `->>`, async `-)`, reply `-->>`, lost `-x`) with `+`/`-` activation
 * suffixes, activate/deactivate, notes, nestable alt/opt/loop/par/critical/break
 * fragments (else/and), and autonumber. Unknown lines warn (never throw).
 *
 * Output feeds `buildSequenceDiagram` (deterministic, dagre-free layout).
 */

export interface SeqParticipant {
  id: string
  name: string
  kind: 'actor' | 'participant'
}

export type SeqEvent =
  | { t: 'msg'; from: string; to: string; label: string; arrow: 'sync' | 'async' | 'reply' | 'lost'; activate?: boolean; deactivate?: boolean }
  | { t: 'note'; placement: 'over' | 'left' | 'right'; parts: string[]; text: string }
  | { t: 'fragStart'; kind: 'alt' | 'opt' | 'loop' | 'par' | 'critical' | 'break'; label?: string }
  | { t: 'fragSection'; label?: string }
  | { t: 'fragEnd' }
  | { t: 'activate'; part: string }
  | { t: 'deactivate'; part: string }

export interface SequenceModel {
  participants: SeqParticipant[]
  events: SeqEvent[]
  autonumber: boolean
}

export interface SequenceParseResult {
  model: SequenceModel
  warnings: string[]
}

export function parseSequenceMermaid(text: string): SequenceParseResult {
  const warnings: string[] = []
  const partMap = new Map<string, SeqParticipant>() // insertion order = first-mention order
  const events: SeqEvent[] = []
  let autonumber = false

  const ensure = (id: string): SeqParticipant => {
    if (!partMap.has(id)) partMap.set(id, { id, name: id, kind: 'participant' })
    return partMap.get(id)!
  }

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line || line.startsWith('%%')) continue
    if (/^sequenceDiagram\b/.test(line)) continue
    if (/^title\b/i.test(line)) continue
    if (/^autonumber\b/i.test(line)) { autonumber = true; continue }
    if (/^box\b/i.test(line)) { warnings.push(`Ignored (box grouping not supported): ${line}`); continue }

    // participant / actor  [as Alias]
    let m = line.match(/^(participant|actor)\s+(\w+)(?:\s+as\s+(.+))?$/i)
    if (m) {
      const p = ensure(m[2])
      p.kind = m[1].toLowerCase() === 'actor' ? 'actor' : 'participant'
      if (m[3]) p.name = m[3].trim()
      continue
    }

    // activate / deactivate
    m = line.match(/^(activate|deactivate)\s+(\w+)$/i)
    if (m) {
      ensure(m[2])
      events.push(m[1].toLowerCase() === 'activate' ? { t: 'activate', part: m[2] } : { t: 'deactivate', part: m[2] })
      continue
    }

    // note over A,B: text  |  note left of A: text  |  note right of A: text
    m = line.match(/^note\s+(over|left of|right of)\s+([\w\s,]+?)\s*:\s*(.*)$/i)
    if (m) {
      const p = m[1].toLowerCase()
      const placement = p.startsWith('left') ? 'left' : p.startsWith('right') ? 'right' : 'over'
      const parts = m[2].split(',').map((s) => s.trim()).filter(Boolean)
      parts.forEach(ensure)
      events.push({ t: 'note', placement, parts, text: m[3].trim() })
      continue
    }

    // fragment start / section / end
    m = line.match(/^(alt|opt|loop|par|critical|break)\b\s*(.*)$/i)
    if (m) { events.push({ t: 'fragStart', kind: m[1].toLowerCase() as any, label: m[2].trim() || undefined }); continue }
    m = line.match(/^(else|and)\b\s*(.*)$/i)
    if (m) { events.push({ t: 'fragSection', label: m[2].trim() || undefined }); continue }
    if (/^end\b/i.test(line)) { events.push({ t: 'fragEnd' }); continue }

    // message:  from <arrow> [+|-] to [: label]
    m = line.match(/^(\w+)\s*(-{1,2}(?:>>|>|\)|x))\s*([+-]?)\s*(\w+)\s*(?::\s*(.*))?$/)
    if (m) {
      const from = m[1], arrowTok = m[2], act = m[3], to = m[4], label = (m[5] ?? '').trim()
      ensure(from); ensure(to)
      let arrow: 'sync' | 'async' | 'reply' | 'lost'
      if (arrowTok.includes('x')) arrow = 'lost'
      else if (arrowTok.includes(')')) arrow = 'async'
      else if (arrowTok.startsWith('--')) arrow = 'reply'
      else arrow = 'sync'
      const ev: SeqEvent = { t: 'msg', from, to, label, arrow }
      if (act === '+') (ev as any).activate = true
      else if (act === '-') (ev as any).deactivate = true
      events.push(ev)
      continue
    }

    warnings.push(`Unparsed line: ${line}`)
  }

  return { model: { participants: [...partMap.values()], events, autonumber }, warnings }
}
